import { useState } from 'react';
import { Alert, Pressable, ScrollView, Text, View } from 'react-native';
import type { BlocoPag, Box } from '../store/types';
import { useDoc } from '../store/store';
import { acrescentarNaPagina } from '../store/estudos';
import { inserirBox, moveBox, removeBox } from '../store/anotacoes';
import { getDoc } from '../store/store';
import { uid } from '../lib/date';
import { Sheet } from './ui';
import { C, alpha } from './theme';
import { t } from '../lib/i18n';

/** Conteúdo da caixa de Anotações convertido em blocos de página de estudo. */
export function blocosDaCaixa(b: Box): BlocoPag[] {
  return b.blocks.map((bl): BlocoPag => bl.type === 'desenho'
    ? { id: uid(), tipo: 'desenho', tracos: bl.tracos ?? [], alt: bl.alt ?? 500 }
    : bl.type === 'text'
    ? { id: uid(), tipo: 'texto', texto: bl.text }
    : { id: uid(), tipo: bl.type === 'check' ? 'check' : 'lista', itens: bl.items.map(i => ({ ...i, id: uid() })) })
    .filter(x => (x.tipo === 'texto' ? x.texto.trim() : x.tipo === 'check' || x.tipo === 'lista' ? x.itens.some(i => i.text.trim()) : x.tipo === 'desenho' ? x.tracos.length > 0 : true));
}

/** Escolhe fichário, matéria e página para onde vai uma caixa de Anotações. */
/** onEnviada: avisa quem chamou (0.52: itens de outras abas, que perguntam se ficam ou saem da origem). */
export function DestinoEstudos({ box, cor, onClose, onEnviada }: { box: Box | null; cor: string; onClose: () => void; onEnviada?: (destino: string) => void }) {
  const { ficharios } = useDoc('estudos');
  const [fid, setFid] = useState<string | null>(null);
  const [mid, setMid] = useState<string | null>(null);
  const [aberto, setAberto] = useState(!!box);
  if (!!box !== aberto) { setAberto(!!box); if (box) { setFid(null); setMid(null); } }
  const f = ficharios.find(x => x.id === fid);
  const m = f?.materias.find(x => x.id === mid);

  const enviar = (pid: string | null) => {
    if (!box || !f || !m) return;
    const titulo = box.title.trim() || t('Anotação');
    acrescentarNaPagina(f.id, m.id, pid, titulo, [...(pid ? [{ id: uid(), tipo: 'texto' as const, texto: titulo }] : []), ...blocosDaCaixa(box)]);
    removeBox(box.id);
    onClose();
    if (onEnviada) onEnviada(`${f.nome} › ${m.nome}`);
    else Alert.alert(t('Enviada'), t('A caixa foi para {destino}.', { destino: `${f.nome} › ${m.nome}` }));
  };
  const Linha = ({ icon, txt, sub, onPress, forte }: { icon: string; txt: string; sub?: string; onPress: () => void; forte?: boolean }) => (
    <Pressable onPress={onPress} style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 14,
      backgroundColor: pressed ? C.card2 : forte ? alpha(cor, 0.18) : C.card })}>
      <Text style={{ fontSize: 22 }}>{icon}</Text>
      <View style={{ flex: 1 }}>
        <Text style={{ color: C.text, fontWeight: '600' }}>{txt}</Text>
        {!!sub && <Text style={{ color: C.sub, fontSize: 12 }}>{sub}</Text>}
      </View>
      <Text style={{ color: C.mute }}>›</Text>
    </Pressable>
  );

  const passo = !f ? t('Escolha o fichário') : !m ? t('Escolha a matéria') : t('Escolha a página');
  return (
    <Sheet visible={!!box} onClose={onClose} title={t('Enviar para Estudos')}>
      <ScrollView contentContainerStyle={{ padding: 16, gap: 8, paddingBottom: 60 }}>
        <Text style={{ color: C.sub }}>{passo}</Text>
        {(f || m) && <Pressable onPress={() => (m ? setMid(null) : setFid(null))}><Text style={{ color: cor }}>‹ {t('Voltar')}</Text></Pressable>}
        {!f && ficharios.map(x => <Linha key={x.id} icon={x.icon} txt={x.nome} sub={t('{n} matéria(s)', { n: x.materias.length })} onPress={() => setFid(x.id)} />)}
        {!f && !ficharios.length && <Text style={{ color: C.sub }}>{t('Você ainda não tem fichários. Crie um na aba Estudos.')}</Text>}
        {f && !m && f.materias.map(x => <Linha key={x.id} icon={x.icon} txt={x.nome} sub={t('{n} página(s)', { n: x.paginas.length })} onPress={() => setMid(x.id)} />)}
        {f && !m && !f.materias.length && <Text style={{ color: C.sub }}>{t('Este fichário ainda não tem matérias.')}</Text>}
        {m && (<>
          <Linha forte icon="➕" txt={t('Nova página com esta caixa')} sub={box?.title || undefined} onPress={() => enviar(null)} />
          {m.paginas.map(p => <Linha key={p.id} icon="📄" txt={p.titulo} sub={t('Acrescentar no fim da página')} onPress={() => enviar(p.id)} />)}
        </>)}
        {!!box && (
          <Pressable onPress={() => {
            if (getDoc('anotacoes').boxes.some(b => b.id === box.id)) moveBox(box.id, 'estudos'); else inserirBox({ ...box, aba: 'estudos' });
            onClose();
            if (onEnviada) onEnviada(t('Estudos'));
            else Alert.alert(t('Enviada'), t('A caixa está em {aba}, em "Anotações recebidas".', { aba: t('Estudos') }));
          }} style={{ padding: 12, marginTop: 8 }}>
            <Text style={{ color: C.sub, textAlign: 'center' }}>{t('Só mandar para a aba Estudos (sem escolher página)')}</Text>
          </Pressable>
        )}
      </ScrollView>
    </Sheet>
  );
}
